import { Button, Modal, sx } from "@dms/ui";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Link as RouterLink } from "react-router-dom";

import { onboardingApi } from "../lib/onboardingApi";
import { OnboardingContext } from "./OnboardingContext";

interface Step {
  id: string;
  title: string;
  body: ReactNode;
}

// Інформаційні кроки (стабільні `id` — прогрес зберігається за id, не номером,
// ADR-042 D4). Навчальний комплект («пограти руками») — наступний слайс.
const STEPS: Step[] = [
  {
    id: "intro",
    title: "Вітаємо у Bukovel-Документообіг!",
    body: (
      <p style={{ margin: 0 }}>
        Коротка екскурсія системою за кілька кроків. Її можна будь-коли пропустити
        й повернутися пізніше — кнопка «Пройти навчання знову» є у вашому профілі.
      </p>
    ),
  },
  {
    id: "navigation",
    title: "Навігація",
    body: (
      <div>
        <p style={{ marginTop: 0 }}>Основні розділи — у меню зліва:</p>
        <ul style={{ margin: 0, paddingLeft: 20, lineHeight: "var(--lh-body, 1.6)" }}>
          <li><strong>Задачі, Процеси, Документи, Проєкти</strong> — робота й документообіг</li>
          <li><strong>Клієнти</strong> (CRM), <strong>Бібліотека файлів</strong>, <strong>Чат</strong></li>
          <li><strong>Пошук</strong> — глобальний, по картках, файлах і довідці</li>
        </ul>
      </div>
    ),
  },
  {
    id: "workflow",
    title: "Як це працює",
    body: (
      <p style={{ margin: 0 }}>
        Звична послідовність: створюєте <strong>документ</strong> або обираете{" "}
        <strong>клієнта</strong> → ставите <strong>задачу</strong> виконавцям → за
        потреби запускаєте <strong>процес</strong> (кроки, погодження, строки).
        Кнопка «Створити» вгорі — швидкий старт.
      </p>
    ),
  },
  {
    id: "done",
    title: "Готово!",
    body: (
      <p style={{ margin: 0 }}>
        Повна довідка завжди під рукою — розділ{" "}
        <RouterLink to="/help" style={{ color: "var(--color-brand-primary)", fontWeight: "var(--fw-medium)" }}>
          «Довідка»
        </RouterLink>{" "}
        у меню (або значок «?» угорі). Навчальний комплект, щоб «пограти руками»,
        зʼявиться незабаром.
      </p>
    ),
  },
];

const FIRST_STEP = STEPS[0].id;

function _failSoft(promise: Promise<unknown>): void {
  // Онбординг НІКОЛИ не блокує застосунок (ADR-042 D4 / NFR-UX-003).
  void promise.catch(() => {});
}

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const autoOpened = useRef(false);

  useEffect(() => {
    let active = true;
    onboardingApi
      .get()
      .then((p) => {
        if (!active) return;
        const idx = STEPS.findIndex((s) => s.id === p.currentStepId);
        if (idx >= 0) setStepIdx(idx);
        // Авто-відкриття лише для NOT_STARTED і лише раз (сервер-стан дає «раз»
        // між вкладками: перша вкладка переводить у IN_PROGRESS).
        if (p.status === "NOT_STARTED" && !autoOpened.current) {
          autoOpened.current = true;
          setStepIdx(0);
          setOpen(true);
          _failSoft(onboardingApi.update({ status: "IN_PROGRESS", currentStepId: FIRST_STEP }));
        }
      })
      .catch(() => {
        /* fail-soft: без стану — просто не показуємо wizard */
      });
    return () => {
      active = false;
    };
  }, []);

  const goTo = useCallback((idx: number) => {
    setStepIdx(idx);
    _failSoft(onboardingApi.update({ currentStepId: STEPS[idx].id }));
  }, []);

  const start = useCallback(() => {
    setStepIdx(0);
    setOpen(true);
    _failSoft(
      onboardingApi.restart().then(() => onboardingApi.update({ currentStepId: FIRST_STEP })),
    );
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    _failSoft(onboardingApi.update({ status: "PAUSED" }));
  }, []);

  const skip = useCallback(() => {
    setOpen(false);
    _failSoft(onboardingApi.update({ status: "SKIPPED" }));
  }, []);

  const finish = useCallback(() => {
    setOpen(false);
    _failSoft(onboardingApi.update({ status: "COMPLETED" }));
  }, []);

  const value = useMemo(() => ({ start }), [start]);
  const step = STEPS[stepIdx];
  const isFirst = stepIdx === 0;
  const isLast = stepIdx === STEPS.length - 1;

  return (
    <OnboardingContext.Provider value={value}>
      {children}
      <Modal
        open={open}
        onClose={close}
        title={step.title}
        width={520}
        footer={
          <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%" }}>
            <Button variant="ghost" onClick={skip}>Пропустити</Button>
            <span style={{ flex: 1, textAlign: "center", fontSize: "var(--fs-meta)", color: "var(--color-text-tertiary)" }}>
              Крок {stepIdx + 1} з {STEPS.length}
            </span>
            {!isFirst && (
              <Button variant="secondary" onClick={() => goTo(stepIdx - 1)}>Назад</Button>
            )}
            {isLast ? (
              <Button variant="primary" onClick={finish}>Завершити</Button>
            ) : (
              <Button variant="primary" onClick={() => goTo(stepIdx + 1)}>Далі</Button>
            )}
          </div>
        }
      >
        <div style={sx({ fontSize: "var(--fs-body)", color: "var(--color-text-primary)", minHeight: 96 })}>
          {step.body}
        </div>
      </Modal>
    </OnboardingContext.Provider>
  );
}
